import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';
import useUpdateAddressCoordinates from '@/api/mutations/addresses/useUpdateAddressCoordinates';
import useGetProjectIntegrations from '@/api/queries/project/useGetProjectIntegrations';
import { usePermissions } from '@/hooks/usePermissions';
import { Alert, Button, Modal, Text, useToastContext } from '@payaca/ambrosia';
import type { ModalProps } from '@payaca/ambrosia';
import {
  type AddressCoordinates,
  type StoredCoordinates,
} from '@payaca/helpers/locationHelper';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { useCallback, useEffect, useMemo, useReducer } from 'react';

import SiteLocationEditorMap from './SiteLocationEditorMap';
import {
  CLOSED_EDITOR_STATE,
  formatList,
  getDistanceBetweenCoordinatesInMetres,
  getEditorPin,
  getIsDirty,
  getLookupGhost,
  getNonUpdatingIntegrationNames,
  siteLocationEditorReducer,
} from './siteLocationEditorState';

type Address = {
  id: string;
  fullLocalAddress?: string | null;
  lookupCoordinates?: StoredCoordinates | null;
  manualCoordinates?: StoredCoordinates | null;
};

type Props = Pick<ModalProps, 'isOpen' | 'onClose'> & {
  address: Address;
  canEdit: boolean;
  projectId: string;
};

const DEFAULT_MAP_CENTRE = { latitude: 51.5074, longitude: -0.1278 };

const SiteLocationEditorModal = (props: Props) => {
  const { address, canEdit, isOpen, onClose, projectId } = props;
  const { pushError, pushSuccess } = useToastContext();
  const { userHasRequiredPermission } = usePermissions();
  const canViewIntegrations = userHasRequiredPermission([
    DealsPermissions.GET_INTEGRATIONS,
  ]);
  const numberFormat = usePreferredNumberFormat({
    maximumFractionDigits: 6,
  });
  const [editorState, dispatch] = useReducer(
    siteLocationEditorReducer,
    CLOSED_EDITOR_STATE
  );
  const { initialManualPin, lookupPin, manualPin } = editorState;
  const isDirty = getIsDirty(editorState);
  const editorAddress = useMemo(
    () => ({
      lookupCoordinates: address.lookupCoordinates,
      manualCoordinates: address.manualCoordinates,
    }),
    [address.lookupCoordinates, address.manualCoordinates]
  );

  const {
    integrations,
    isError: integrationsError,
    isLoading: integrationsLoading,
  } = useGetProjectIntegrations(
    isOpen && canEdit && canViewIntegrations ? projectId : undefined
  );
  const integrationsNotUpdated = useMemo(
    () =>
      getNonUpdatingIntegrationNames(
        integrations?.map((integration) => integration.service) ?? []
      ),
    [integrations]
  );

  useEffect(() => {
    dispatch(
      isOpen
        ? {
            type: 'addressSynced',
            addressId: address.id,
            address: editorAddress,
          }
        : { type: 'closed' }
    );
  }, [address.id, editorAddress, isOpen]);

  const { mutate: updateCoordinates, isPending } = useUpdateAddressCoordinates(
    projectId,
    {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        pushSuccess('Site location saved');
        onClose?.();
      },
      onError: () => {
        pushError("We couldn't save the site location");
      },
    }
  );

  const pin = useMemo(
    () => getEditorPin(manualPin, lookupPin),
    [lookupPin, manualPin]
  );
  const lookupGhost = useMemo(
    () => getLookupGhost(manualPin, lookupPin),
    [lookupPin, manualPin]
  );
  const canSave =
    canEdit && isDirty && (manualPin !== null || initialManualPin !== null);
  const lookupDistance =
    manualPin && lookupPin
      ? getDistanceBetweenCoordinatesInMetres(manualPin, lookupPin)
      : null;

  const handlePositionChange = useCallback(
    (coordinates: AddressCoordinates) => {
      if (!canEdit || isPending) return;
      dispatch({ type: 'pinMoved', coordinates });
    },
    [canEdit, isPending]
  );

  const handleSave = () => {
    if (!canSave || isPending) return;

    updateCoordinates({
      id: address.id,
      coordinates: manualPin,
    });
  };
  const mapInstructions = (() => {
    if (!canEdit) {
      return 'This site location is shown for reference. The map is read-only.';
    }
    if (!pin) return 'No active location is available to adjust.';
    return 'Drag the active marker onto the correct building.';
  })();

  return (
    <Modal
      title="Site location"
      isOpen={isOpen}
      onClose={isPending ? undefined : onClose}
      size="lg"
    >
      <>
        <Modal.Body>
          <div className="flex flex-col gap-4" aria-busy={isPending}>
            {address.fullLocalAddress && (
              <Text>{address.fullLocalAddress}</Text>
            )}

            <Text level="supporting">{mapInstructions}</Text>

            <SiteLocationEditorMap
              centre={pin ?? DEFAULT_MAP_CENTRE}
              disabled={isPending}
              editable={canEdit}
              lookupPin={lookupGhost}
              onPositionChange={handlePositionChange}
              pin={pin}
            />

            <div
              aria-label="Map marker legend"
              className="flex flex-wrap items-start gap-x-4 gap-y-2"
              data-testid="site-location-legend"
              role="group"
            >
              <div className="flex min-w-0 flex-1 items-start gap-x-4">
                <div className="flex shrink-0 items-center gap-2">
                  <div
                    aria-hidden="true"
                    className="h-3 w-3 rounded-full bg-purple-600"
                  />
                  <Text as="span">Active location</Text>
                </div>
                {lookupGhost && (
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div
                      aria-hidden="true"
                      className="h-3 w-3 shrink-0 rounded-full border-2 border-solid border-gray-500 opacity-80"
                    />
                    <div className="min-w-0">
                      <Text as="span">
                        Address lookup location
                        {lookupDistance !== null &&
                          ` · ${numberFormat.format(lookupDistance)} metres away`}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
              {pin && (
                <div className="ml-auto shrink-0 whitespace-nowrap text-right">
                  <Text as="span">
                    {numberFormat.format(pin.latitude)},{' '}
                    {numberFormat.format(pin.longitude)}
                  </Text>
                </div>
              )}
            </div>

            {canEdit &&
              canViewIntegrations &&
              integrationsNotUpdated.length > 0 && (
                <Alert colour="yellow" iconName="alert-triangle.3">
                  This correction will not be sent to the linked design{' '}
                  {integrationsNotUpdated.length === 1 ? 'tool' : 'tools'}:{' '}
                  {formatList(integrationsNotUpdated)}. You can still save the
                  site location here.
                </Alert>
              )}

            {canEdit && canViewIntegrations && integrationsLoading && (
              <Text level="supporting">
                Checking linked design tools. You can still save the site
                location.
              </Text>
            )}

            {canEdit && canViewIntegrations && integrationsError && (
              <Alert colour="yellow" iconName="alert-triangle.3">
                We couldn&apos;t check linked design tools. You can still save
                the site location.
              </Alert>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions className="flex-col items-stretch gap-y-2 sm:flex-row sm:items-center sm:justify-end">
            {canEdit ? (
              <>
                {manualPin && (
                  <Button
                    className="w-full sm:w-auto"
                    variant="secondary"
                    onClick={() => dispatch({ type: 'pinReset' })}
                    disabled={isPending}
                  >
                    Reset to lookup
                  </Button>
                )}
                <Button
                  className="w-full sm:w-auto"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  onClick={handleSave}
                  disabled={!canSave || isPending}
                  isProcessing={isPending}
                  replaceContentWhenProcessing
                  aria-label={
                    isPending ? 'Saving site location' : 'Save location'
                  }
                  aria-busy={isPending}
                >
                  Save location
                </Button>
              </>
            ) : (
              <Button
                className="w-full sm:w-auto"
                variant="secondary"
                onClick={onClose}
              >
                Close
              </Button>
            )}
          </Modal.Footer.Actions>
        </Modal.Footer>
      </>
    </Modal>
  );
};

export default SiteLocationEditorModal;
